import React, { useMemo, useState } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const PAGE_SIZE = 20;

const STATUS_STYLES = {
  active:   'bg-green-100 text-green-700',
  inactive: 'bg-gray-100 text-gray-500',
  unknown:  'bg-muted text-muted-foreground',
};
const STATUS_LABELS = {
  active:   'Aktiv',
  inactive: 'Inaktiv',
  unknown:  'Okänd',
};

const SortIcon = ({ field, sortField, sortDir }) => {
  if (sortField !== field) return <ChevronsUpDown size={13} className="opacity-40" />;
  return sortDir === 'asc'
    ? <ChevronUp size={13} className="text-primary" />
    : <ChevronDown size={13} className="text-primary" />;
};

/**
 * ActivityTable
 *
 * Props:
 *  rows     [{ id, name, attendancePct, goalsCount, meetingsCount, lastActivity, status }]
 *  loading  bool
 */
const ActivityTable = ({ rows = [], loading = false }) => {
  const [search, setSearch]       = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir]     = useState('asc');
  const [page, setPage]           = useState(1);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setPage(1);
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return q ? rows.filter(r => r.name.toLowerCase().includes(q)) : rows;
  }, [rows, search]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let va = a[sortField];
      let vb = b[sortField];
      if (va === null || va === undefined) va = sortDir === 'asc' ? Infinity : -Infinity;
      if (vb === null || vb === undefined) vb = sortDir === 'asc' ? Infinity : -Infinity;
      if (typeof va === 'string') return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === 'asc' ? va - vb : vb - va;
    });
  }, [filtered, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paged = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const COLUMNS = [
    { key: 'name',          label: 'Namn'              },
    { key: 'attendancePct', label: 'Närvaro%'           },
    { key: 'goalsCount',    label: 'Aktiva mål'         },
    { key: 'meetingsCount', label: 'Möten'              },
    { key: 'lastActivity',  label: 'Senaste aktivitet'  },
    { key: 'status',        label: 'Status'             },
  ];

  return (
    <div className="space-y-3" data-testid="activity-table">
      {/* Search */}
      <div className="flex items-center gap-2">
        <Input
          data-testid="activity-search"
          placeholder="Sök deltagare..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          className="max-w-xs h-9 text-sm"
        />
        {search && (
          <button
            type="button"
            data-testid="clear-search"
            onClick={() => setSearch('')}
            className="text-xs text-muted-foreground hover:text-foreground underline"
          >
            Rensa
          </button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/40">
              {COLUMNS.map(col => (
                <th
                  key={col.key}
                  className="text-left py-2.5 px-3 font-bold text-xs uppercase tracking-wide text-muted-foreground whitespace-nowrap"
                >
                  <button
                    type="button"
                    data-testid={`sort-${col.key}`}
                    onClick={() => handleSort(col.key)}
                    className="flex items-center gap-1 hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring rounded-sm transition-colors"
                    aria-label={`Sortera på ${col.label}`}
                  >
                    {col.label}
                    <SortIcon field={col.key} sortField={sortField} sortDir={sortDir} />
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="border-b" data-testid={`skeleton-row-${i}`}>
                  {COLUMNS.map(col => (
                    <td key={col.key} className="py-2.5 px-3">
                      <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
                    </td>
                  ))}
                </tr>
              ))
            ) : paged.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length} className="py-10 text-center text-muted-foreground text-sm"
                  data-testid="activity-empty-state">
                  {search
                    ? `Ingen deltagare matchar "${search}"`
                    : 'Ingen aktivitetsdata för vald period'
                  }
                </td>
              </tr>
            ) : (
              paged.map(row => {
                const status = row.status || 'unknown';
                return (
                  <tr
                    key={row.id}
                    data-testid={`activity-row-${row.id}`}
                    className="border-b hover:bg-muted/20 transition-colors"
                  >
                    <td className="py-2.5 px-3 font-medium" style={{ color: '#333333' }}>
                      {row.name}
                    </td>
                    <td className="py-2.5 px-3 text-center" data-testid={`attendance-${row.id}`}>
                      {row.attendancePct !== null ? `${row.attendancePct}%` : '–'}
                    </td>
                    <td className="py-2.5 px-3 text-center" data-testid={`goals-${row.id}`}>
                      {row.goalsCount ?? 0}
                    </td>
                    <td className="py-2.5 px-3 text-center" data-testid={`meetings-${row.id}`}>
                      {row.meetingsCount ?? 0}
                    </td>
                    <td className="py-2.5 px-3 text-muted-foreground" data-testid={`last-activity-${row.id}`}>
                      {row.lastActivity ?? '–'}
                    </td>
                    <td className="py-2.5 px-3" data-testid={`status-${row.id}`}>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${STATUS_STYLES[status] || STATUS_STYLES.unknown}`}>
                        {STATUS_LABELS[status] || 'Okänd'}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!loading && sorted.length > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-muted-foreground"
          data-testid="pagination">
          <span>{sorted.length} deltagare totalt</span>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="page-prev"
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
              className="rounded-full"
            >
              Föregående
            </Button>
            <span data-testid="page-indicator">{page} / {totalPages}</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="page-next"
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
              className="rounded-full"
            >
              Nästa
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityTable;
